import {ShallowChanged, Timer} from "js-vextensions";
import {EngineConfig_General} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_General.js";
import {LiveSession} from "../../LiveSession.js";
import {EngineSessionComp, StatusUIObj} from "./EngineSessionComp.js";
import {minuteInMS, secondInMS} from "web-vcore";
import {WebAudioPlayerPool} from "../../../Utils/Media/WebAudioPlayer.js";
import {Device} from "@capacitor/device";
import {EffectPointerPlayer} from "../../../Server/DB/@Shared/EffectPointerPlayer.js";
import {EngineConfig_Alerts} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_Alerts.js";
import {BreathComp} from "./BreathComp.js";
import {Row} from "react-vcomponents";
import React from "react";
import {AddSchema} from "mobx-graphlink";

// note: alert sources must have prefix "alert_"
export const EffectSource_values = [
	"alert_batteryLow",
	"alert_breath_beltDC",
	"alert_breath_beltLoose",
	"breath_sync"
] as const;
export type EffectSource = typeof EffectSource_values[number];
AddSchema("EffectSource", {enum: EffectSource_values as any});
// Note: Higher layers get "applied later", eg. if L20 sets a light's target-brightness (even to 0%), that overrides L10's attempted set.
// However: If multiple active sources exist in the same layer, the highest target-brightness among those sources is used.
export const effectSource_info: {[source in EffectSource]: {layer: number}} = {
	alert_batteryLow: {layer: 20},
	alert_breath_beltDC: {layer: 20},
	alert_breath_beltLoose: {layer: 20},
	breath_sync: {layer: 10},
};
export const EffectSource_values_alerts = EffectSource_values.filter(a=>a.startsWith("alert_")) as EffectSource[];

class ActiveEffectSourceState {
	lightBrightness: number|n;
}
type EffectSourceState = ActiveEffectSourceState|null|undefined;
export const EffectState_Light = (sourceActive: boolean, targetBrightnessWhenSourceActive: number|null, invalidateTargetBrightnessIfBelow: number|null)=>{
	if (!sourceActive) return null;
	const targetBrightnessValid = invalidateTargetBrightnessIfBelow == null ? true :
		(targetBrightnessWhenSourceActive == null || targetBrightnessWhenSourceActive >= invalidateTargetBrightnessIfBelow);
	// return an object (meaning source is active), but in case of target-brightness being invalid, don't set a target-brightness (ie. don't apply a light-effect at all)
	return {lightBrightness: targetBrightnessValid ? targetBrightnessWhenSourceActive : null};
};

export class AlertsComp extends EngineSessionComp<EngineConfig_Alerts> {
	constructor(session: LiveSession, config: EngineConfig_Alerts) {
		super(session, config, s=>true, s=>s.IsLocal());
	}

	override GetStatusUI(): StatusUIObj {
		return {
			ui: <Row mt={5}>Alerts... active: {this.GetActiveAlerts().join(", ")}</Row>,
			omitWrapper: true,
		};
	}

	override OnActivate() {
		this.light_player.effectPointer = this.c.lightEffectPointer;
		this.light_player.OnStart();
		this.baseEffectApplyTimer.Start();
		this.batteryCheckTimer.Start();
	}
	override OnDeactivate(sessionStopping: boolean) {
		this.light_player.Stop();
		this.baseEffectApplyTimer.Stop();
		this.batteryCheckTimer.Stop();
	}

	// general effect system (including alert system)
	// ==========

	baseEffectApplyTimer_alertEffectsExternallyUpdatedSinceLastTick = false;
	baseEffectApplyTimer = new Timer(this.c.effectBaseApplyInterval * 1000, ()=>{
		// if there was another/external source that triggered an effect-update in-between ticks of this timer...
		// then skip this update (since this is just meant as a fallback in case no externally-sourced updates occur)
		if (this.baseEffectApplyTimer_alertEffectsExternallyUpdatedSinceLastTick) {
			this.baseEffectApplyTimer_alertEffectsExternallyUpdatedSinceLastTick = false;
			return;
		}
		this.UpdateEffects("baseApplyInterval");
	});

	/** If value is null or undefined, source (/alarm) is inactive. (null = currently inactive, undefined = inactive since session creation)
	 * If value is non-null, source is active, with the following properties settable:
	 * 1) A target light-brightness (0-1; or null, if light-effect disabled) */
	effectSourceStates = new Map<EffectSource, EffectSourceState>();
	GetActiveAlerts() {
		const activeAlertSources = [] as EffectSource[];
		for (const source of EffectSource_values) {
			if (source.startsWith("alert_") && this.effectSourceStates.get(source) != null) activeAlertSources.push(source);
		}
		return activeAlertSources;
	}
	SetEffectState(source: EffectSource, state: EffectSourceState, updateEffectsIfStateChanged = true) {
		if (!this.effectSourceStates.has(source)) this.effectSourceStates.set(source, null);

		const oldState = this.effectSourceStates.get(source);
		if (ShallowChanged(oldState, state)) {
			this.effectSourceStates.set(source, state);
			if (updateEffectsIfStateChanged) this.ScheduleUpdateOfEffects();

			const oldActive = oldState != null;
			const newActive = state != null;
			if (oldActive != newActive && source.startsWith("alert_") && this.c.events_addForAlertTypes?.includes(source)) {
				this.s.AsLocal!.AddEvent({type: "Generic", alerts_setAlertActive: {source, active: newActive}});
			}
		}
		return this; // for chaining
	}

	scheduledEffectUpdate_timeoutId: number|n;
	ScheduleUpdateOfEffects() {
		//if (this.scheduledEffectUpdate_timeoutId != null) clearTimeout(this.scheduledEffectUpdate_timeoutId);
		if (this.scheduledEffectUpdate_timeoutId != null) return; // if one already scheduled, do nothing
		this.scheduledEffectUpdate_timeoutId = setTimeout(()=>{
			this.scheduledEffectUpdate_timeoutId = null;
			this.UpdateEffects("externallyScheduledUpdate");
		}, 0) as any as number;
	}

	lastEffectUpdate_finalTargetBrightness: number|n;
	UpdateEffects(source: "externallyScheduledUpdate"|"baseApplyInterval") {
		const effectSourceLayers = Object.values(effectSource_info).map(a=>a.layer).Distinct().OrderBy(a=>a);
		let finalTargetBrightness: number|n;
		for (const layer of effectSourceLayers) {
			const effectSourceStatesInLayer = [...this.effectSourceStates].filter(a=>effectSource_info[a[0]].layer == layer);
			let brightnessToApplyForLayer: number|n;
			for (const [_source, state] of effectSourceStatesInLayer) {
				if (state?.lightBrightness != null) {
					brightnessToApplyForLayer = (brightnessToApplyForLayer ?? 0).KeepAtLeast(state.lightBrightness);
				}
			}
			if (brightnessToApplyForLayer != null) {
				finalTargetBrightness = brightnessToApplyForLayer;
			}
		}

		// if there is at least one effect-source trying to set light-brightness, apply that
		if (finalTargetBrightness != null) {
			this.light_player.Play(finalTargetBrightness);
		}
		// else, if there *had* been an effect-source doing so at last check, reset light now
		else if (this.lastEffectUpdate_finalTargetBrightness != null) {
			this.light_player.Play(0);
		}

		if (source == "externallyScheduledUpdate") {
			this.baseEffectApplyTimer_alertEffectsExternallyUpdatedSinceLastTick = true;
		}
		this.lastEffectUpdate_finalTargetBrightness = finalTargetBrightness;
	}

	// alerts managed by this comp
	// ==========

	light_player = new EffectPointerPlayer();
	batteryCheckTimer = new Timer(10_000, ()=>this.CheckBatteryLowForAlert());

	lastBatteryCheck_batteryLevel: number|n;
	slowChargingLastConfirmedAt: number|n;
	async CheckBatteryLowForAlert() {
		const batteryInfo = await Device.getBatteryInfo().catch(()=>null);
		if (batteryInfo?.batteryLevel == null) return;

		const batteryLevel = batteryInfo.batteryLevel;
		const isCharging_final = batteryInfo.isCharging || (()=>{
			if (!this.c.batteryLow_slowChargeDetection) return false;
			if (this.lastBatteryCheck_batteryLevel == null) return false; // if last battery-level unknown, we can't infer slow-charging

			// It seems that some slower charging sources don't cause "isCharging" to become true!
			// So also do our own check: If battery-level has increased since last check, consider device to be charging anyway.
			const slowChargingConfirmedNow = !batteryInfo.isCharging && batteryLevel > this.lastBatteryCheck_batteryLevel;

			// Also infer (continued) slow-charging, if battery level hasn't dropped, and we confirmed an instance of slow-charging recently.
			const slowChargingConfirmedRecently = this.slowChargingLastConfirmedAt != null && Date.now() - this.slowChargingLastConfirmedAt < 5 * minuteInMS;
			const slowChargingInferred = slowChargingConfirmedRecently && batteryLevel >= this.lastBatteryCheck_batteryLevel;

			if (slowChargingConfirmedNow) this.slowChargingLastConfirmedAt = Date.now();
			return slowChargingConfirmedNow || slowChargingInferred;
		})();

		const alertShouldBeActive = batteryLevel < this.c.batteryLow_threshold && !isCharging_final;
		this.SetEffectState("alert_batteryLow", EffectState_Light(alertShouldBeActive, this.c.batteryLow_lockLightsToBrightness, null));

		this.lastBatteryCheck_batteryLevel = batteryLevel;
	}
}